
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Mail, User, RefreshCw } from "lucide-react";
import { useCouponUsers } from "@/hooks/useCouponUsers";
import { useIsMobile } from "@/hooks/use-mobile";
import { format } from "date-fns";

interface CouponUsersDialogProps {
  couponId: string;
  couponCode: string;
  usedCount: number;
}

const CouponUsersDialog = ({ couponId, couponCode, usedCount }: CouponUsersDialogProps) => {
  const [open, setOpen] = useState(false);
  const { data: couponUsers, isLoading, refetch } = useCouponUsers(couponId);
  const isMobile = useIsMobile();

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const getUserDisplayName = (user: any) => {
    if (user?.first_name || user?.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user?.email || 'Unknown User';
  };

  // Use the actual count from the database query
  const actualUserCount = couponUsers?.length || 0;

  const handleRefresh = () => {
    refetch();
  };

  const triggerButton = (
    <Button variant="outline" size="sm">
      <Users className="h-4 w-4 mr-2" />
      View Linked Users ({actualUserCount})
    </Button>
  );

  const headerContent = (
    <>
      <div className="flex items-center justify-between">
        <span>Linked Accounts - {couponCode}</span>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleRefresh}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    </>
  );

  const descriptionContent = (
    <>
      View all accounts that have linked this coupon to their profile.
      <br />
      <span className="text-xs text-gray-500">
        Database count: {actualUserCount} users have linked this coupon
      </span>
    </>
  );

  const mainContent = (
    <div className="space-y-4">
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading linked accounts...</p>
          </div>
        </div>
      ) : couponUsers && couponUsers.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Linked Accounts</h4>
            <Badge variant="secondary">{couponUsers.length} total</Badge>
          </div>

          <div className="border rounded-lg overflow-hidden">
            {isMobile ? (
              <div className="space-y-2 p-4">
                {couponUsers.map((couponUser) => (
                  <div key={couponUser.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="font-medium text-sm">
                        {getUserDisplayName(couponUser.profiles)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span className="text-xs text-gray-600">
                        {couponUser.profiles?.email || 'No email'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs">
                        {formatDate(couponUser.applied_at)}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {couponUser.times_used} uses
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Linked Date</TableHead>
                    <TableHead>Times Used</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {couponUsers.map((couponUser) => (
                    <TableRow key={couponUser.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">
                            {getUserDisplayName(couponUser.profiles)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            {couponUser.profiles?.email || 'No email'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatDate(couponUser.applied_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {couponUser.times_used}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No accounts have linked this coupon yet</p>
          <p className="text-sm mt-2">Users will appear here once they link the coupon</p>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          {triggerButton}
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[90vh]">
          <SheetHeader>
            <SheetTitle>
              {headerContent}
            </SheetTitle>
            <SheetDescription>
              {descriptionContent}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {mainContent}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {triggerButton}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {headerContent}
          </DialogTitle>
          <DialogDescription>
            {descriptionContent}
          </DialogDescription>
        </DialogHeader>
        {mainContent}
      </DialogContent>
    </Dialog>
  );
};

export default CouponUsersDialog;
